import { useCallback, useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { formatDistanceToNow } from "date-fns";
import {
  ChevronDown,
  Copy,
  Download,
  FileUp,
  MoreVertical,
  Pencil,
  Plus,
  Trash2,
} from "lucide-react";
import { toast } from "sonner";

import logo from "@/assets/logo.svg";
import { AccountMenu } from "@/auth/AccountMenu";
import { DownloadDesktopApp } from "@/components/DownloadDesktopApp";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Input } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";
import { duplicateDeck } from "@/lib/deckBundle";
import { subscribeDeckMessages } from "@/lib/deckChannel";
import {
  DECK_FILE_EXTENSION,
  DeckFileError,
  exportDeckFile,
  importDeckFile,
} from "@/lib/deckFile";
import { isDeckOpenElsewhere } from "@/lib/deckSession";
import {
  deleteDeckCascade,
  getDeck,
  listDecks,
  putDeck,
  type DeckIndexEntry,
} from "@/lib/idb";
import { createNewDeck } from "@/store/bootstrap";

// Another page broadcasts `deck-updated` on every persisted keystroke. The list only shows
// a title and a relative timestamp, so it can afford to lag a moment and re-read once.
const REFRESH_DEBOUNCE_MS = 300;

// `/documents` — every deck side by side (TODO §12.4.1).
//
// Rendered entirely from the `deckIndex` store, never from `decks`: a `getAll("decks")`
// would deserialize every slide and stylesheet in the database just to draw a row of cards
// (§12.3.1). The thumbnail on each card is the first active slide's, captured by the
// editor's offscreen capturer and stashed on the index row (§12.3.2).
export function DocumentsHome() {
  const navigate = useNavigate();
  const [decks, setDecks] = useState<DeckIndexEntry[] | null>(null);
  const [busy, setBusy] = useState(false);
  const [renaming, setRenaming] = useState<DeckIndexEntry | null>(null);
  const [renameValue, setRenameValue] = useState("");
  const [deleting, setDeleting] = useState<DeckIndexEntry | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const refresh = useCallback(async () => {
    setDecks(await listDecks());
  }, []);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  // §12.5.3 Any deck change anywhere — another window editing, deleting, importing — makes
  // this list stale, and a stale documents home is how a user loses track of their own work.
  useEffect(() => {
    let timer = 0;
    const unsubscribe = subscribeDeckMessages(() => {
      window.clearTimeout(timer);
      timer = window.setTimeout(() => void refresh(), REFRESH_DEBOUNCE_MS);
    });
    return () => {
      window.clearTimeout(timer);
      unsubscribe();
    };
  }, [refresh]);

  // §12.4.3 New document — blank, or straight into the Gridd Library panel. (Duplicate, the
  // third entry point, is per-card below since it needs a deck to copy.)
  const newBlank = async () => {
    setBusy(true);
    try {
      navigate(`/d/${await createNewDeck()}`);
    } finally {
      setBusy(false);
    }
  };
  const newFromLibrary = async () => {
    setBusy(true);
    try {
      navigate(`/d/${await createNewDeck()}?panel=library`);
    } finally {
      setBusy(false);
    }
  };

  const duplicate = async (entry: DeckIndexEntry) => {
    const copyId = await duplicateDeck(entry.deckId);
    if (!copyId) {
      toast.error("That deck is no longer here");
      void refresh();
      return;
    }
    await refresh();
    toast.success(`Duplicated “${entry.title}”`);
  };

  const exportDeck = async (entry: DeckIndexEntry) => {
    if (!(await exportDeckFile(entry.deckId))) {
      toast.error("That deck is no longer here");
      void refresh();
    }
  };

  const openRename = async (entry: DeckIndexEntry) => {
    // Renaming a deck another window is actively editing would bump its `rev` out from
    // under it and cost that window its next save (§12.5.4). Point the user at the window
    // that actually owns the deck instead.
    if (await isDeckOpenElsewhere(entry.deckId)) {
      toast.error("That deck is open in another window", {
        description: "Rename it there, or close that window first.",
      });
      return;
    }
    setRenameValue(entry.title);
    setRenaming(entry);
  };

  const commitRename = async () => {
    const entry = renaming;
    const title = renameValue.trim();
    setRenaming(null);
    if (!entry || !title || title === entry.title) return;
    const deck = await getDeck(entry.deckId);
    if (!deck) {
      toast.error("That deck is no longer here");
      void refresh();
      return;
    }
    const result = await putDeck({
      ...deck,
      title,
      meta: { ...deck.meta, updatedAt: new Date().toISOString() },
    });
    if (result === "conflict") {
      toast.error("That deck changed while you were renaming it");
    }
    await refresh();
  };

  const commitDelete = async () => {
    const entry = deleting;
    setDeleting(null);
    if (!entry) return;
    // §12.4.4 Cascades to grid bodies, image blobs and the index row. A page with this deck
    // open hears `deck-deleted` and stops showing it.
    await deleteDeckCascade(entry.deckId);
    await refresh();
    toast.success(`Deleted “${entry.title}”`);
  };

  const importFile = async (file: File) => {
    setBusy(true);
    try {
      // §12.6.2 Import always remaps ids into a NEW deck — importing a file exported from
      // this same database must not overwrite the deck it came from.
      const { deckId, adoptedNuggets } = await importDeckFile(file);
      await refresh();
      // §17.5.3 Adoption is worth a sentence: the imported deck is now sharing slides with
      // nuggets this machine already had, and the local copies were kept — which is the right
      // rule and a surprising one to discover by accident.
      toast.success(`Imported “${file.name}”`, {
        description:
          adoptedNuggets > 0
            ? `${adoptedNuggets} nugget${adoptedNuggets === 1 ? "" : "s"} already on this device — linked to your copy rather than replacing it.`
            : undefined,
      });
      navigate(`/d/${deckId}`);
    } catch (err) {
      toast.error(
        err instanceof DeckFileError
          ? err.message
          : "That file couldn’t be imported",
      );
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="app-screen-min flex flex-col bg-slate-100">
      <header className="flex items-center gap-6 bg-slate-900 px-6 py-3 text-white">
        <Link to="/" className="flex items-center gap-8">
          <img src={logo} alt="DeckAI" className="h-8 w-auto" />
        </Link>
        <span className="flex-1 text-sm font-medium">Documents</span>
        <div className="flex items-center gap-3">
          <input
            ref={fileInputRef}
            type="file"
            accept={`${DECK_FILE_EXTENSION},application/zip`}
            className="hidden"
            onChange={(e) => {
              const file = e.target.files?.[0];
              // Clear first: picking the same file twice in a row must fire again.
              e.target.value = "";
              if (file) void importFile(file);
            }}
          />
          <Button
            variant="secondary"
            size="sm"
            className="gap-2"
            disabled={busy}
            onClick={() => fileInputRef.current?.click()}
          >
            <FileUp className="h-4 w-4" /> Import
          </Button>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button size="sm" className="gap-2" disabled={busy}>
                <Plus className="h-4 w-4" /> New document
                <ChevronDown className="h-4 w-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem onClick={newBlank}>
                Blank document
              </DropdownMenuItem>
              <DropdownMenuItem onClick={newFromLibrary}>
                From the Gridd Library
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
          {/* §14.11.4 Website only — the component removes itself inside the shell. */}
          <DownloadDesktopApp />
          <AccountMenu />
        </div>
      </header>

      <main className="flex-1 px-6 py-8">
        <div className="mx-auto max-w-6xl">
          {decks === null ? (
            <CardGrid>
              {[0, 1, 2, 3].map((i) => (
                <Skeleton key={i} className="h-56 rounded-lg" />
              ))}
            </CardGrid>
          ) : decks.length === 0 ? (
            <EmptyState onNew={newBlank} busy={busy} />
          ) : (
            <CardGrid>
              {decks.map((entry) => (
                <DeckCard
                  key={entry.deckId}
                  entry={entry}
                  onOpen={() => navigate(`/d/${entry.deckId}`)}
                  onRename={() => void openRename(entry)}
                  onDuplicate={() => void duplicate(entry)}
                  onExport={() => void exportDeck(entry)}
                  onDelete={() => setDeleting(entry)}
                />
              ))}
            </CardGrid>
          )}
        </div>
      </main>

      <Dialog
        open={renaming !== null}
        onOpenChange={(open) => !open && setRenaming(null)}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Rename document</DialogTitle>
            <DialogDescription>
              This is the name on the card and in the exported file.
            </DialogDescription>
          </DialogHeader>
          <Input
            value={renameValue}
            autoFocus
            onChange={(e) => setRenameValue(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") void commitRename();
            }}
            aria-label="Document name"
          />
          <DialogFooter>
            <Button variant="outline" onClick={() => setRenaming(null)}>
              Cancel
            </Button>
            <Button onClick={() => void commitRename()}>Rename</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog
        open={deleting !== null}
        onOpenChange={(open) => !open && setDeleting(null)}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              Delete “{deleting?.title || "Untitled deck"}”?
            </AlertDialogTitle>
            <AlertDialogDescription>
              The deck, its grids and its images are removed from this browser.
              This cannot be undone — export it first if you want a copy.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Keep it</AlertDialogCancel>
            <AlertDialogAction onClick={() => void commitDelete()}>
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

function CardGrid({ children }: { children: React.ReactNode }) {
  return (
    <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
      {children}
    </div>
  );
}

function DeckCard({
  entry,
  onOpen,
  onRename,
  onDuplicate,
  onExport,
  onDelete,
}: {
  entry: DeckIndexEntry;
  onOpen: () => void;
  onRename: () => void;
  onDuplicate: () => void;
  onExport: () => void;
  onDelete: () => void;
}) {
  return (
    <div className="group relative flex flex-col overflow-hidden rounded-lg border border-slate-200 bg-white shadow-sm transition-shadow hover:shadow-md">
      <button
        onClick={onOpen}
        className="block w-full text-left"
        aria-label={`Open ${entry.title || "Untitled deck"}`}
      >
        <div className="aspect-video w-full border-b border-slate-100 bg-slate-50">
          {entry.thumbDataUrl ? (
            <img
              src={entry.thumbDataUrl}
              alt=""
              className="h-full w-full object-contain"
            />
          ) : (
            <div className="flex h-full items-center justify-center text-xs text-slate-400">
              No preview yet
            </div>
          )}
        </div>
        <div className="px-4 pt-3">
          <div className="truncate text-sm font-medium text-slate-900">
            {entry.title || "Untitled deck"}
          </div>
        </div>
      </button>
      <div className="flex items-center justify-between gap-2 px-4 pb-3 pt-1">
        <span className="truncate text-xs text-slate-500">
          {relativeTime(entry.updatedAt)} · {entry.slideCount}{" "}
          {entry.slideCount === 1 ? "slide" : "slides"}
        </span>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button
              variant="ghost"
              size="icon"
              className="h-7 w-7 shrink-0"
              aria-label={`Actions for ${entry.title || "Untitled deck"}`}
            >
              <MoreVertical className="h-4 w-4" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <DropdownMenuItem onClick={onOpen}>Open</DropdownMenuItem>
            <DropdownMenuItem onClick={onRename}>
              <Pencil className="mr-2 h-4 w-4" /> Rename…
            </DropdownMenuItem>
            <DropdownMenuItem onClick={onDuplicate}>
              <Copy className="mr-2 h-4 w-4" /> Duplicate
            </DropdownMenuItem>
            <DropdownMenuItem onClick={onExport}>
              <Download className="mr-2 h-4 w-4" /> Export {DECK_FILE_EXTENSION}
            </DropdownMenuItem>
            <DropdownMenuSeparator />
            <DropdownMenuItem
              onClick={onDelete}
              className="text-red-600 focus:bg-red-50 focus:text-red-700"
            >
              <Trash2 className="mr-2 h-4 w-4" /> Delete
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </div>
  );
}

function EmptyState({ onNew, busy }: { onNew: () => void; busy: boolean }) {
  return (
    <div className="rounded-lg border border-dashed border-slate-300 bg-white px-6 py-16 text-center">
      <h2 className="text-base font-semibold text-slate-900">No documents</h2>
      <p className="mx-auto mt-2 max-w-md text-sm text-slate-600">
        Decks live in this browser on this device. Start one, or import a{" "}
        <code className="rounded bg-slate-100 px-1">{DECK_FILE_EXTENSION}</code>{" "}
        file you exported earlier.
      </p>
      <Button className="mt-6 gap-2" onClick={onNew} disabled={busy}>
        <Plus className="h-4 w-4" /> New document
      </Button>
    </div>
  );
}

// A stored timestamp can be malformed (hand-edited storage, a truncated import), and a card
// is not worth throwing a render for.
function relativeTime(iso: string): string {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return "Updated recently";
  return `Updated ${formatDistanceToNow(date, { addSuffix: true })}`;
}
